import { makeAutoObservable } from 'mobx'
import { type Call } from '@twilio/voice-sdk'
import { logger } from 'shared/lib'
import { sentEventSentry } from 'entities/Sentry/lib/sentEventSentry'
import { type CallEntry } from 'entities/Call/model/CallEntry'

// Inbound conference create re-anchors the agent: the BE dials the agent's
// client identity via the Participants API (no custom params possible) into
// the conference, then hangs up the original agent leg. The window is armed
// before the create request — the new leg can ring before the HTTP response
// returns. This store owns the window state machine; CallStore delegates the
// map/teardown operations through the host.
type IPendingConferenceReanchor = {
  oldCallSid: string
  inboxNumber: string
  call: Call | null
  // The re-dialed leg's exact sid from the create response; null until the
  // response arrives (or forever, on an older backend without the field).
  expectedCallSid: string | null
  oldLegDisconnected: boolean
  timeoutId: ReturnType<typeof setTimeout>
}

type IConferenceReanchorHost = {
  getEntry: (callSid: string) => CallEntry | undefined
  rekeyEntry: (entry: CallEntry, newCallSid: string) => void
  teardownEndedCall: (entry: CallEntry, callSid: string) => void
  log: (title: string) => void
}

type IReanchorCancelReason =
  | 'timeout'
  | 'request-failed'
  | 'user-end'
  | 're-arm'
  | 'leg-failed'
  | 'call-ended'
  | 'fallback'
  | 'entry-gone'

export class ConferenceReanchorStore {
  private _pending: IPendingConferenceReanchor | null = null
  private _lastReanchoredSids: { oldCallSid: string; newCallSid: string } | null = null
  // Legs this store disconnected itself (reconciliation, cancel). Their late
  // 'accept' events must abort the accept flow — running it would mark the
  // tab busy for a call that no longer exists.
  private readonly _droppedLegs = new WeakSet<Call>()
  // Covers the create round-trip + the re-dialed leg's ring; also bounds how
  // long a deferred teardown can wait when the re-anchor never completes.
  private readonly _PENDING_TIMEOUT_MS = 10000
  // The BE dials the re-anchor leg before responding, so once the create
  // response is in with no leg rung, only a short grace is justified.
  private readonly _RESPONSE_GRACE_TIMEOUT_MS = 3000

  constructor(private _host: IConferenceReanchorHost) {
    makeAutoObservable(this)
  }

  // The conference UI captures the agent leg's sid at create time; after a
  // commit that sid is retired — resolve it to the live leg's sid.
  resolveRetiredCallSid = (callSid: string): string => {
    const mapping = this._lastReanchoredSids
    return mapping && mapping.oldCallSid === callSid ? mapping.newCallSid : callSid
  }

  // Arm BEFORE the conference-create request goes out (the re-dialed leg can
  // ring before the response). Inbound only — outbound never re-dials.
  begin = (oldCallSid: string, inboxNumber: string) => {
    this.cancel('re-arm')
    const timeoutId = setTimeout(() => this._onTimeout(), this._PENDING_TIMEOUT_MS)
    this._pending = {
      oldCallSid,
      inboxNumber,
      call: null,
      expectedCallSid: null,
      oldLegDisconnected: false,
      timeoutId,
    }
  }

  // Terminal for an uncommitted window; runs the deferred teardown if the
  // original leg already died while the window held it back.
  cancel = (reason: IReanchorCancelReason) => {
    const pending = this._pending
    if (!pending) return
    clearTimeout(pending.timeoutId)
    this._pending = null
    if (pending.call) {
      this._dropLeg(pending.call)
    }
    this._teardownIfOldLegDied(pending)
    this._sentCancelledEvent(reason, pending)
  }

  // Create-response verdict on the re-dialed leg: sid → match strictly (an
  // already-taken leg is verified retroactively); null → BE fallback, drop
  // the window.
  applyCreateResponse = (oldCallSid: string, reanchoredCallSid: string | null) => {
    const pending = this._pending

    if (pending && pending.oldCallSid === oldCallSid) {
      if (reanchoredCallSid === null) {
        this.cancel('fallback')
        return
      }

      pending.expectedCallSid = reanchoredCallSid

      if (pending.call) {
        const acceptedSid = pending.call.parameters.CallSid
        if (acceptedSid === reanchoredCallSid) return

        // The heuristic took the wrong leg — drop it and free the slot so
        // the named leg can still be accepted while the window is armed.
        this._verifyAcceptedSid(acceptedSid, reanchoredCallSid)
        const wrongLeg = pending.call
        pending.call = null
        this._dropLeg(wrongLeg)
      }

      this._shortenGraceIfLegNotArrived()
      return
    }

    // Already committed — verify the swapped-in leg (null contradicts the
    // swap just like a wrong sid).
    const mapping = this._lastReanchoredSids
    if (mapping && mapping.oldCallSid === oldCallSid) {
      this._verifyAcceptedSid(mapping.newCallSid, reanchoredCallSid)
    }
  }

  acceptLegIfPending = (connect: Call): boolean => {
    const pending = this._pending
    if (!pending || pending.call) return false

    // Once the create response named the leg, the heuristic is off — only the
    // exact sid is accepted; anything else falls through to the quiet ignore.
    if (pending.expectedCallSid && connect.parameters.CallSid !== pending.expectedCallSid) {
      return false
    }

    pending.call = connect
    connect.accept()

    sentEventSentry({
      event: '[Twilio:Calling] Conference re-anchor: leg auto-accepted',
      name: 'calling',
      level: 'log',
      tags: {
        callSid: connect.parameters.CallSid,
        oldCallSid: pending.oldCallSid,
        fromMatchesInbox: String(this._numbersMatch(connect.parameters.From, pending.inboxNumber)),
      },
      sampleRate: 1.0,
    })
    this._host.log('[Twilio] - eventDeviceIncoming (re-anchor leg auto-accepted)')
    return true
  }

  // Commit-on-accept: swap the re-dialed leg into the existing entry and
  // re-key the map, so the original leg's later disconnect misses the lookup
  // and cannot tear the call down. 'aborted' = the entry is gone and the leg
  // was disconnected here — the caller must not continue the accept flow.
  commitLegIfPending = (connect: Call): 'none' | 'committed' | 'aborted' => {
    // A leg this store already dropped can still emit a late 'accept'.
    if (this._droppedLegs.has(connect)) return 'aborted'

    const pending = this._pending
    if (!pending || pending.call !== connect) return 'none'

    const sid = connect.parameters.CallSid

    // The create response named a different leg while this one was ringing —
    // drop it and keep the window armed for the named leg.
    if (pending.expectedCallSid && sid !== pending.expectedCallSid) {
      this._verifyAcceptedSid(sid, pending.expectedCallSid)
      pending.call = null
      this._dropLeg(connect)
      return 'aborted'
    }

    clearTimeout(pending.timeoutId)
    this._pending = null

    const entry = this._host.getEntry(pending.oldCallSid)
    if (!entry) {
      this._dropLeg(connect)
      this._sentCancelledEvent('entry-gone', pending)
      return 'aborted'
    }

    // The re-dialed leg starts unmuted — carry the mute state over.
    const wasMuted = entry.call.isMuted()
    const oldKey = entry.callSid
    entry.replaceCall(connect)
    if (sid && oldKey !== sid) {
      this._host.rekeyEntry(entry, sid)
      this._lastReanchoredSids = { oldCallSid: oldKey, newCallSid: sid }
    }
    if (wasMuted) {
      connect.mute(true)
    }
    return 'committed'
  }

  // The re-dialed leg failed before connecting; the original leg, if alive,
  // keeps the call going. Same terminal as cancel — disconnecting the already
  // dead leg there is a safe no-op.
  revertLegIfPending = (call: Call): boolean => {
    const pending = this._pending
    if (!pending || pending.call !== call) return false
    this.cancel('leg-failed')
    return true
  }

  // With pending.call set this disconnect is the BE retiring the original leg
  // — defer the teardown; without it the customer genuinely hung up.
  // Do NOT mark the entry sdkLegClosed: that nulls primaryEntry and the popup
  // item gets deleted before the commit lands.
  deferOldLegTeardown = (callSid: string): boolean => {
    const pending = this._pending
    if (!pending || !pending.call) return false
    if (callSid !== pending.oldCallSid) return false
    pending.oldLegDisconnected = true
    return true
  }

  dispose = () => {
    if (this._pending) {
      clearTimeout(this._pending.timeoutId)
      this._pending = null
    }
  }

  // The create response arrived but the re-dialed leg never rang — most
  // likely the BE fallback; shrink the remaining wait instead of holding the
  // full window.
  private _shortenGraceIfLegNotArrived = () => {
    const pending = this._pending
    if (!pending || pending.call) return
    clearTimeout(pending.timeoutId)
    pending.timeoutId = setTimeout(() => this._onTimeout(), this._RESPONSE_GRACE_TIMEOUT_MS)
  }

  private _dropLeg = (call: Call) => {
    this._droppedLegs.add(call)
    try {
      call.disconnect()
    } catch (e) {
      logger.error(e)
    }
  }

  private _onTimeout = () => {
    // BE fallback keeps the original leg and never dials a new one — the
    // armed window simply expires.
    this.cancel('timeout')
  }

  private _sentCancelledEvent = (
    reason: IReanchorCancelReason,
    pending: IPendingConferenceReanchor
  ) => {
    sentEventSentry({
      event: '[Twilio:Calling] Conference re-anchor: window cancelled',
      name: 'calling',
      level: 'log',
      tags: {
        reason,
        legArrived: String(!!pending.call),
        oldLegDied: String(pending.oldLegDisconnected),
      },
      sampleRate: 1.0,
    })
  }

  private _verifyAcceptedSid = (acceptedSid: string, expectedSid: string | null) => {
    if (acceptedSid === expectedSid) return
    sentEventSentry({
      event: '[Twilio:Calling] Conference re-anchor: accepted leg sid mismatch',
      name: 'calling',
      level: 'error',
      tags: {
        acceptedSid,
        expectedSid: expectedSid ?? 'none',
      },
      sampleRate: 1.0,
    })
  }

  private _teardownIfOldLegDied = (pending: IPendingConferenceReanchor) => {
    if (!pending.oldLegDisconnected) return
    const entry = this._host.getEntry(pending.oldCallSid)
    if (entry) {
      this._host.teardownEndedCall(entry, pending.oldCallSid)
    }
  }

  private _numbersMatch = (numberA?: string, numberB?: string): boolean => {
    if (!numberA || !numberB) return false
    const digitsA = numberA.replace(/\D/g, '')
    const digitsB = numberB.replace(/\D/g, '')
    if (!digitsA || !digitsB) return false
    return digitsA.endsWith(digitsB) || digitsB.endsWith(digitsA)
  }
}
